<template>
    <div id="asset-operations-datatable-widget-component">
        <WidgetWrapperComponent
            :title="$t('asset_operations_datatable_widget_component.operations')"
            id-widget="asset-operations-datatable-widget"
        >
            <template v-slot:options>
                <menu-actions-component
                    :actions="[
                        {
                            title: $t('asset_operations_datatable_widget_component.download_filtered_operations'),
                            icon: 'mdi-download',
                            emit: 'downloadFilteredOperations',
                            has_access: userHasAccess(asset_module.designation, role_vision),
                        },
                        {
                            title: $t('asset_operations_datatable_widget_component.download_all_operations'),
                            icon: 'mdi-download',
                            emit: 'downloadAllOperations',
                            has_access: userHasAccess(asset_module.designation, role_vision),
                        },
                        {
                            title: $t('asset_operations_datatable_widget_component.add_operation'),
                            icon: 'mdi-plus',
                            emit: 'openAddOperationDialog',
                            has_access: has_access_to_actions,
                        },
                    ]"
                    v-on:downloadAllOperations="getOperationsCsv(false)"
                    v-on:downloadFilteredOperations="getOperationsCsv(true)"
                    v-on:openAddOperationDialog="$refs.addEditOperationDialog.openAddOperationDialog()"
                />
            </template>

            <template v-slot:default>
                <transition name="fade" mode="out-in">
                    <div v-if="!buildingHeader" key="datatable">
                        <date-range-picker-component :date_range="date_range" class="mb-4" v-on:update="handleDateRangeUpdate" />
                        <v-data-table :headers="headers" :items="operations" :loading="fetchingOperations">
                            <template v-slot:item.date_operation_start="{ item }">
                                <chips-last-statement :date="item.date_operation_start" color="disable" raw-display size="small" />
                            </template>
                            <template v-slot:item.origin="{ item }">
                                {{
                                    item.origin === 'MANUAL'
                                        ? $t('asset_operations_datatable_widget_component.manual')
                                        : $t('asset_operations_datatable_widget_component.auto')
                                }}
                            </template>
                            <template v-slot:item.key_operation="{ item }">
                                <v-chip :color="item.key_operation === 'tank_flush' ? 'error' : 'success'" outlined>
                                    {{ $t(`key_operation.${item.key_operation}`) }}
                                </v-chip>
                            </template>
                            <template v-slot:item.measure_before_operation="{ item }">
                                {{ displayToHumanNumberWithSpace(item.measure_before_operation) }} L
                            </template>
                            <template v-slot:item.measure_after_operation="{ item }">
                                {{ displayToHumanNumberWithSpace(item.measure_after_operation) }} L
                            </template>
                            <template v-slot:item.delta_volume="{ item }">
                                {{ displayToHumanNumberWithSpace(item.delta_volume) }} L
                            </template>
                            <template v-slot:item.price="{ item }"> {{ displayToHumanNumberWithSpace(item.price) }} € </template>
                            <template v-slot:item.price_per_hundred_liters="{ item }">
                                {{ displayToHumanNumberWithSpace(item.price_per_hundred_liters) }} €
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <div class="d-flex justify-end">
                                    <menu-actions-component
                                        :actions="[
                                            {
                                                title: $t('asset_operations_datatable_widget_component.edit_operation'),
                                                icon: 'mdi-pencil-outline',
                                                emit: 'openEditOperationDialog',
                                                has_access: has_access_to_actions,
                                            },
                                            {
                                                title: $t('asset_operations_datatable_widget_component.delete_operation'),
                                                icon: 'mdi-trash-can',
                                                emit: 'openDeleteOperationDialog',
                                                has_access: has_access_to_actions,
                                                color: 'error',
                                            },
                                        ]"
                                        v-on:openEditOperationDialog="$refs.addEditOperationDialog.openEditOperationDialog(item)"
                                        v-on:openDeleteOperationDialog="$refs.deleteOperationDialog.openDeleteOperationDialog(item)"
                                    />
                                </div>
                            </template>
                        </v-data-table>
                    </div>
                    <div v-else key="no-operation">
                        {{ $t('asset_operations_datatable_widget_component.no_operation') }}
                    </div>
                </transition>
            </template>
        </WidgetWrapperComponent>

        <add-edit-operation-dialog-component
            ref="addEditOperationDialog"
            :asset="asset"
            v-on:added:operation="fetchAssetOperationsDatatable"
            v-on:updated:operation="fetchAssetOperationsDatatable"
        />
        <delete-operation-dialog-component ref="deleteOperationDialog" v-on:deleted:operation="fetchAssetOperationsDatatable" />
    </div>
</template>

<script>
import assetRepository from '@/repositories/AssetRepository'
import moment from 'moment/moment'
import displayHuman from '@/mixin/displayHuman'
import ChipsLastStatement from '@/components/global/containment/ChipsLastStatement.vue'
import DateRangePickerComponent from '@/components/global/picker/DateRangePickerComponent.vue'
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import MenuActionsComponent from '@/components/global/containment/MenuActionsComponent.vue'
import AddEditOperationDialogComponent from '@/components/equipment/generic/form/AddEditOperationDialogComponent.vue'
import DeleteOperationDialogComponent from '@/components/equipment/generic/form/DeleteOperationDialogComponent.vue'

export default {
    name: 'AssetOperationsDatatableWidgetComponent',
    mixins: [displayHuman],
    components: {
        DateRangePickerComponent,
        ChipsLastStatement,
        WidgetWrapperComponent,
        MenuActionsComponent,
        AddEditOperationDialogComponent,
        DeleteOperationDialogComponent,
    },
    props: {
        asset: {
            type: Object,
            required: true,
        },
        asset_module: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            date_range: {
                startDate: moment().clone().startOf('day').subtract(1, 'day').toDate(),
                endDate: moment().toDate(),
            },
            buildingHeader: true,
            operations: [],
            fetchingOperations: true,
            fetching_csv: false,
            manageFillUpDialog: null,
            headers: [
                {
                    text: this.$t('date_operation_begin'),
                    value: 'date_operation_start',
                    key: 'date_operation_start',
                    key_database: 'date_operation_start',
                    visible: true,
                    configurable: true,
                    sortable: true,
                },
                {
                    text: this.$t('asset_operations_datatable_widget_component.operation_type'),
                    value: 'origin',
                    key: 'origin',
                    key_database: 'origin',
                    visible: true,
                    sortable: true,
                },
                {
                    text: this.$t('asset_operations_datatable_widget_component.description'),
                    value: 'key_operation',
                    key: 'key_operation',
                    key_database: 'id_ref_intervention',
                    visible: true,
                    sortable: true,
                },
                {
                    text: this.$t('use_chart.measure_before_operations'),
                    value: 'measure_before_operation',
                    key: 'measure_before_operation',
                    key_database: 'mesure_avant_operation',
                    visible: true,
                    filterable: true,
                },
                {
                    text: this.$t('use_chart.measure_after_operations'),
                    value: 'measure_after_operation',
                    key: 'measure_after_operation',
                    key_database: 'mesure_apres_operation',
                    visible: true,
                    filterable: true,
                },
                {
                    text: this.$t('Delta'),
                    value: 'delta_volume',
                    key: 'delta_volume',
                    key_database: 'delta_volume',
                    visible: true,
                    filterable: true,
                },
                {
                    text: this.$t('asset_operations_datatable_widget_component.total_price'),
                    value: 'price',
                    key: 'price',
                    key_database: 'prix',
                    visible: true,
                    sortable: true,
                },
                {
                    text: this.$t('asset_operations_datatable_widget_component.price_per_100_liters'),
                    value: 'price_per_hundred_liters',
                    key: 'price_per_hundred_liters',
                    key_database: 'price_per_hundred_liters',
                    visible: true,
                    sortable: true,
                },
                { text: this.$t('global.actions'), value: 'actions', filterable: false, sortable: false, align: 'end' },
            ],
        }
    },
    computed: {
        has_access_to_actions() {
            return this.userHasAccess(this.asset_module.designation, this.role_admin)
        },
    },
    async mounted() {
        await this.fetchAssetOperationsDatatable()
    },
    methods: {
        async fetchAssetOperationsDatatable() {
            this.fetchingOperations = true
            const params = new URLSearchParams({
                date_start: moment(this.date_range.startDate).format('YYYY-MM-DD 00:00:00'),
                date_end: moment(this.date_range.endDate).format('YYYY-MM-DD 23:59:59'),
            })
            await assetRepository
                .getOperationsBetweenDateRange(this.asset.id_asset, params)
                .then((success) => {
                    this.operations = success.data.data.sort((a, b) => {
                        return a.date_operation_start < b.date_operation_start ? 1 : -1
                    })
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetchingOperations = false
                    this.buildingHeader = false
                })
        },
        getOperationsCsv(filtered) {
            this.fetching_csv = true
            const params = new URLSearchParams()
            if (filtered) {
                params.append('start_date', moment(this.date_range.startDate).format('YYYY-MM-DD'))
                params.append('end_date', moment(this.date_range.endDate).format('YYYY-MM-DD'))
            }
            assetRepository
                .getOperationsCsv(this.asset.id_asset, params)
                .then((success) => {
                    this.downloadFileFromBlob(success)
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetching_csv = false
                })
        },
        handleDateRangeUpdate(dates) {
            this.date_range = dates
            this.fetchAssetOperationsDatatable()
        },
    },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
